import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaEye, FaSearch, FaFilter, FaSort, FaDownload, FaUpload, FaCalendar } from 'react-icons/fa';
import { MdRefresh, MdDeleteForever, MdEdit, MdVisibility } from 'react-icons/md';
import styles from './Settings.module.scss';
import StoreForm from '../store-form/StoreForm';
import API_BASE_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext.jsx';

const Settings = () => {
    const { makeAuthenticatedRequest } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterField, setFilterField] = useState('all');
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedItem, setSelectedItem] = useState(null);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    // New state for Nepali date filtering and item name dropdown
    const [dateFilterType, setDateFilterType] = useState('english'); // 'english' or 'nepali'
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedItemName, setSelectedItemName] = useState('');
    const [uniqueItemNames, setUniqueItemNames] = useState([]);
    
    // New state for quantity filters (now controls pagination limit)
    const [selectedQuantityFilter, setSelectedQuantityFilter] = useState('');
    const [quantityFilterOptions, setQuantityFilterOptions] = useState([
        { value: '', label: 'All Items' },
        { value: '1', label: '1 Item' },
        { value: '100', label: '100 Items' },
        { value: '200', label: '200 Items' },
        { value: '300', label: '300 Items' },
        { value: '500', label: '500 Items' },
        { value: '20000', label: '20000 Items' }
    ]);

    // Fetch items from API with filters
    const fetchItems = async (page = 1) => {
        try {
            setLoading(true);
            
            // Determine the limit based on selected quantity filter
            let limit = itemsPerPage;
            if (selectedQuantityFilter) {
                if (selectedQuantityFilter === '') {
                    // "All Items" - set a very large limit to get all items
                    limit = 1000000;
                } else {
                    // Use the selected value as the limit
                    limit = parseInt(selectedQuantityFilter);
                }
            }
            
            // Build query parameters based on current filters
            const params = new URLSearchParams();
            params.append('limit', limit);
            params.append('offset', (page - 1) * limit);
            
            // Add filter parameters (excluding quantity as it's now used for pagination)
            if (searchTerm) params.append('search', searchTerm);
            if (selectedItemName) params.append('item_name', selectedItemName);
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);
            if (dateFilterType) params.append('date_filter_type', dateFilterType);
            if (sortField) params.append('sort_field', sortField);
            if (sortDirection) params.append('sort_direction', sortDirection);
            
            const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/?${params.toString()}`);
            if (!response.ok) {
                throw new Error('Failed to fetch items');
            }
            const data = await response.json();
            setItems(data.results || []);
            setTotalPages(Math.ceil(data.count / limit));
            setCurrentPage(page);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, [searchTerm, selectedItemName, startDate, endDate, dateFilterType, sortField, sortDirection]);

    // Handle quantity filter changes (pagination limit)
    useEffect(() => {
        fetchItems(1); // Reset to first page when quantity filter changes
    }, [selectedQuantityFilter]);

    // Fetch unique item names for dropdown
    const fetchUniqueItemNames = async () => {
        try {
            const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/item-names/`);
            if (response.ok) {
                const data = await response.json();
                console.log('Fetched unique item names:', data.item_names);
                setUniqueItemNames(data.item_names || []);
            } else {
                console.error('Failed to fetch unique item names - response not ok:', response.status);
            }
        } catch (err) {
            console.error('Failed to fetch unique item names:', err);
        }
    };

    // Fetch unique quantities for dropdown (no longer needed as we use hardcoded pagination limits)
    const fetchUniqueQuantities = async () => {
        // This function is no longer needed as quantity filter now controls pagination limit
        // Keeping it for potential future use
    };

    // Fetch unique item names on component mount
    useEffect(() => {
        fetchUniqueItemNames();
        // fetchUniqueQuantities(); // No longer needed as quantity filter controls pagination
    }, []);

    // Nepali date conversion functions
    const convertEnglishToNepaliDate = (englishDate) => {
        if (!englishDate) return '';
        
        try {
            const date = new Date(englishDate);
            const year = date.getFullYear();
            const month = date.getMonth() + 1;
            const day = date.getDate();
            
            // Convert to Nepali year (add 57)
            const nepaliYear = year + 57;
            
            // Convert digits to Nepali
            const nepaliToEnglishDigits = {
                '0': '०', '1': '१', '2': '२', '3': '३', '4': '४',
                '5': '५', '6': '६', '7': '७', '8': '८', '9': '९'
            };
            
            const convertToNepaliDigits = (num) => {
                return String(num).split('').map(digit => nepaliToEnglishDigits[digit] || digit).join('');
            };
            
            const nepaliYearStr = convertToNepaliDigits(nepaliYear);
            const nepaliMonthStr = convertToNepaliDigits(String(month).padStart(2, '0'));
            const nepaliDayStr = convertToNepaliDigits(String(day).padStart(2, '0'));
            
            return `${nepaliYearStr}-${nepaliMonthStr}-${nepaliDayStr}`;
        } catch (error) {
            return englishDate;
        }
    };

    const convertNepaliToEnglishDate = (nepaliDate) => {
        if (!nepaliDate) return '';
        
        try {
            // Convert Nepali digits to English digits
            const nepaliToEnglishDigits = {
                '०': '0', '१': '1', '२': '2', '३': '3', '४': '4',
                '५': '5', '६': '6', '७': '7', '८': '8', '९': '9'
            };
            
            let englishDate = nepaliDate;
            Object.keys(nepaliToEnglishDigits).forEach(nepaliDigit => {
                englishDate = englishDate.replace(new RegExp(nepaliDigit, 'g'), nepaliToEnglishDigits[nepaliDigit]);
            });
            
            // Parse Nepali date (format: yyyy-mm-dd)
            const parts = englishDate.split('-');
            if (parts.length !== 3) return nepaliDate;
            
            const nepaliYear = parseInt(parts[0]);
            const nepaliMonth = parseInt(parts[1]);
            const nepaliDay = parseInt(parts[2]);
            
            // Validate the parsed values
            if (isNaN(nepaliDay) || isNaN(nepaliMonth) || isNaN(nepaliYear)) {
                return nepaliDate;
            }
            
            // Convert Nepali year to English year (subtract 57)
            const englishYear = nepaliYear - 57;
            
            // Format as English date (yyyy-mm-dd)
            return `${englishYear}-${String(nepaliMonth).padStart(2, '0')}-${String(nepaliDay).padStart(2, '0')}`;
        } catch (error) {
            return nepaliDate;
        }
    };

    // Delete item
    const deleteItem = async (id) => {
        try {
            const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/${id}/`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                throw new Error('Failed to delete item');
            }
            fetchItems(currentPage);
            setShowDeleteConfirm(false);
            setItemToDelete(null);
        } catch (err) {
            setError(err.message);
        }
    };

    // Update item
    const updateItem = async (id, updatedData) => {
        try {
            const response = await makeAuthenticatedRequest(`${API_BASE_URL}/api/items/${id}/`, {
                method: 'PUT',
                body: JSON.stringify(updatedData),
            });
            if (!response.ok) {
                throw new Error('Failed to update item');
            }
            fetchItems(currentPage);
            setShowEditForm(false);
            setSelectedItem(null);
        } catch (err) {
            setError(err.message);
        }
    };

    // Handle edit
    const handleEdit = (item) => {
        console.log('Editing item:', item.specification, 'ID:', item.id);
        setSelectedItem(item);
        setShowEditForm(true);
    };

    // Handle delete confirmation
    const handleDelete = (item) => {
        setItemToDelete(item);
        setShowDeleteConfirm(true);
    };

    // Handle view
    const handleView = (item) => {
        setSelectedItem(item);
        setShowEditForm(true);
    };

    // Clear all filters
    const clearAllFilters = () => {
        setSearchTerm('');
        setSelectedItemName('');
        setStartDate('');
        setEndDate('');
        setFilterField('all');
        setSelectedQuantityFilter('');
    };

    // Check if any filters are active
    const hasActiveFilters = searchTerm || selectedItemName || startDate || endDate || selectedQuantityFilter;

    // Items are now filtered and sorted by the backend
    const filteredAndSortedItems = items;

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        
        if (dateFilterType === 'nepali') {
            return convertEnglishToNepaliDate(dateString);
        } else {
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    };

    // Handle form submission from edit form
    const handleFormSubmit = (formData) => {
        if (selectedItem) {
            updateItem(selectedItem.id, formData);
        }
    };

    // Handle Excel download
    const handleDownloadExcel = async () => {
        try {
            // Build query parameters based on current filters
            const params = new URLSearchParams();
            
            if (searchTerm) params.append('search', searchTerm);
            if (selectedItemName) params.append('item_name', selectedItemName);
            if (startDate) params.append('start_date', startDate);
            if (endDate) params.append('end_date', endDate);
            if (dateFilterType) params.append('date_filter_type', dateFilterType);
            if (sortField) params.append('sort_field', sortField);
            if (sortDirection) params.append('sort_direction', sortDirection);
            
            const url = `${API_BASE_URL}/api/items/download-excel/?${params.toString()}`;
            
            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `store_items_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
        } catch (error) {
            console.error('Download error:', error);
            alert('Failed to download Excel file. Please try again.');
        }
    };

    if (showEditForm && selectedItem) {
        console.log('Selected item for editing:', selectedItem);
        return (
            <div className={styles.settingsContainer}>
                <div className={styles.editFormHeader}>
                    <h2>Edit Item</h2>
                    <button 
                        className={styles.backButton}
                        onClick={() => {
                            setShowEditForm(false);
                            setSelectedItem(null);
                        }}
                    >
                        ← Back to List
                    </button>
                </div>
                <StoreForm 
                    initialData={selectedItem}
                    onSubmit={handleFormSubmit}
                    isEditMode={true}
                />
            </div>
        );
    }

    return (
        <div className={styles.settingsContainer}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    <h1>Store Management Settings</h1>
                    <p>Manage and organize your inventory data</p>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.downloadButton} onClick={handleDownloadExcel}>
                        <FaDownload /> Download Excel
                    </button>
                    <button className={styles.refreshButton} onClick={() => fetchItems(currentPage)}>
                        <MdRefresh /> Refresh
                    </button>
                </div>
            </div>

            {/* Search and Filter Bar */}
            <div className={styles.searchFilterBar}>
                {/* Filter Summary */}
                {hasActiveFilters && (
                    <div className={styles.filterSummary}>
                        <span className={styles.filterSummaryLabel}>Active Filters:</span>
                        <div className={styles.activeFilters}>
                            {searchTerm && (
                                <span className={styles.filterTag}>
                                    Search: "{searchTerm}" <button onClick={() => setSearchTerm('')}>×</button>
                                </span>
                            )}
                            {selectedItemName && (
                                <span className={styles.filterTag}>
                                    Item: {selectedItemName} <button onClick={() => setSelectedItemName('')}>×</button>
                                </span>
                            )}
                            {selectedQuantityFilter && (
                                <span className={styles.filterTag}>
                                    Items per page: {selectedQuantityFilter === '' ? 'All' : selectedQuantityFilter} <button onClick={() => setSelectedQuantityFilter('')}>×</button>
                                </span>
                            )}
                            {(startDate || endDate) && (
                                <span className={styles.filterTag}>
                                    Date: {startDate || 'Start'} - {endDate || 'End'} 
                                    <button onClick={() => { setStartDate(''); setEndDate(''); }}>×</button>
                                </span>
                            )}
                        </div>
                    </div>
                )}
                
                {/* Top Row - Filters */}
                <div className={styles.filterSection}>
                    {/* Item Name Dropdown */}
                    <select 
                        value={selectedItemName} 
                        onChange={(e) => setSelectedItemName(e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="">All Items (जिन्सी सामानको नाम)</option>
                        {uniqueItemNames.map((name, index) => (
                            <option key={index} value={name}>{name}</option>
                        ))}
                    </select>

                    {/* Quantity Filter */}
                    <select 
                        value={selectedQuantityFilter} 
                        onChange={(e) => setSelectedQuantityFilter(e.target.value)}
                        className={styles.filterSelect}
                    >
                        {quantityFilterOptions.map((option, index) => (
                            <option key={index} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    {/* Date Filter Type Toggle */}
                    <div className={styles.dateFilterToggle}>
                        <button 
                            className={`${styles.dateTypeButton} ${dateFilterType === 'english' ? styles.active : ''}`}
                            onClick={() => setDateFilterType('english')}
                        >
                            English Date
                        </button>
                        <button 
                            className={`${styles.dateTypeButton} ${dateFilterType === 'nepali' ? styles.active : ''}`}
                            onClick={() => setDateFilterType('nepali')}
                        >
                            Nepali Date (नेपाली मिति)
                        </button>
                    </div>

                    {/* Date Range Filters */}
                    <div className={styles.dateRangeSection}>
                        <div className={styles.dateInput}>
                            <FaCalendar className={styles.dateIcon} />
                            <input
                                type="text"
                                placeholder={dateFilterType === 'nepali' ? 'Start Date (yyyy-mm-dd)' : 'Start Date'}
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className={styles.dateInputField}
                            />
                        </div>
                        <div className={styles.dateInput}>
                            <FaCalendar className={styles.dateIcon} />
                            <input
                                type="text"
                                placeholder={dateFilterType === 'nepali' ? 'End Date (yyyy-mm-dd)' : 'End Date'}
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className={styles.dateInputField}
                            />
                        </div>
                    </div>
                    
                    <select 
                        value={sortField} 
                        onChange={(e) => setSortField(e.target.value)}
                        className={styles.sortSelect}
                    >
                        <option value="created_at">Date Created</option>
                        <option value="specification">Specification</option>
                        <option value="dakshila_no">Entry No</option>
                        <option value="saman_ko_pahichan">Item ID</option>
                        <option value="model_no">Model No</option>
                        <option value="item_name">Item Name</option>
                    </select>
                    
                    <button 
                        className={styles.sortButton}
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                    >
                        <FaSort /> {sortDirection === 'asc' ? '↑' : '↓'}
                    </button>

                    {/* Clear Filters Button */}
                    <button 
                        className={`${styles.clearFiltersButton} ${hasActiveFilters ? styles.hasFilters : ''}`}
                        onClick={clearAllFilters}
                    >
                        <FaFilter /> Clear Filters
                    </button>
                </div>

                {/* Middle Row - Search Box */}
                <div className={styles.searchSection}>
                    <div className={styles.searchBox}>
                        <FaSearch className={styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Search items..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                        />
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className={styles.errorMessage}>
                    <span>⚠️ {error}</span>
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p>Loading items...</p>
                </div>
            )}

            {/* Items List */}
            {!loading && (
                <div className={styles.itemsContainer}>
                    {filteredAndSortedItems.length === 0 ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>📦</div>
                            <h3>No items found</h3>
                            <p>Try adjusting your search or filters</p>
                        </div>
                    ) : (
                        <div className={styles.itemsGrid}>
                            {filteredAndSortedItems.map((item) => (
                                <div key={item.id} className={styles.itemCard}>
                                    <div className={styles.itemHeader}>
                                        <div className={styles.itemTitle}>
                                            <h3>{item.specification || 'No Specification'}</h3>
                                            <span className={styles.itemId}>ID: {item.id}</span>
                                        </div>
                                        <div className={styles.itemActions}>
                                            <button 
                                                className={`${styles.actionButton} ${styles.viewButton}`}
                                                onClick={() => handleView(item)}
                                                title="View Details"
                                            >
                                                <MdVisibility />
                                            </button>
                                            <button 
                                                className={`${styles.actionButton} ${styles.editButton}`}
                                                onClick={() => handleEdit(item)}
                                                title="Edit Item"
                                            >
                                                <MdEdit />
                                            </button>
                                            <button 
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                onClick={() => handleDelete(item)}
                                                title="Delete Item"
                                            >
                                                <MdDeleteForever />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className={styles.itemDetails}>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>जिन्सी सामानको नाम:</span>
                                            <span className={styles.detailValue}>{item.item_name || '-'}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Entry No:</span>
                                            <span className={styles.detailValue}>{item.dakshila_no || '-'}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Item ID:</span>
                                            <span className={styles.detailValue}>{item.saman_ko_pahichan || '-'}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Model:</span>
                                            <span className={styles.detailValue}>{item.model_no || '-'}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Quantity:</span>
                                            <span className={styles.detailValue}>{item.parikshan || '-'}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Rate:</span>
                                            <span className={styles.detailValue}>{item.prati_ekai_ko_dar || '-'}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.detailLabel}>Total Value:</span>
                                            <span className={styles.detailValue}>{item.jamma_paral_mulya || '-'}</span>
                                        </div>
                                    </div>
                                    
                                    <div className={styles.itemFooter}>
                                        <span className={styles.createdDate}>
                                            Created: {formatDate(item.created_at)}
                                        </span>
                                        <span className={styles.updatedDate}>
                                            Updated: {formatDate(item.updated_at)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <div className={styles.pagination}>
                    <button 
                        className={styles.paginationButton}
                        disabled={currentPage === 1}
                        onClick={() => fetchItems(currentPage - 1)}
                    >
                        ← Previous
                    </button>
                    
                    <div className={styles.pageNumbers}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                                key={page}
                                className={`${styles.pageButton} ${currentPage === page ? styles.activePage : ''}`}
                                onClick={() => fetchItems(page)}
                            >
                                {page}
                            </button>
                        ))}
                    </div>
                    
                    <button 
                        className={styles.paginationButton}
                        disabled={currentPage === totalPages}
                        onClick={() => fetchItems(currentPage + 1)}
                    >
                        Next →
                    </button>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && itemToDelete && (
                <div className={styles.modalOverlay}>
                    <div className={styles.confirmationModal}>
                        <div className={styles.modalHeader}>
                            <h3>Confirm Delete</h3>
                            <button 
                                className={styles.closeButton}
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setItemToDelete(null);
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.modalContent}>
                            <div className={styles.warningIcon}>⚠️</div>
                            <p>Are you sure you want to delete this item?</p>
                            <div className={styles.itemPreview}>
                                <strong>{itemToDelete.specification || 'No Specification'}</strong>
                                <span>ID: {itemToDelete.id}</span>
                            </div>
                            <p className={styles.warningText}>
                                This action cannot be undone. The item will be permanently removed from the database.
                            </p>
                        </div>
                        <div className={styles.modalActions}>
                            <button 
                                className={styles.cancelButton}
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setItemToDelete(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                className={styles.deleteConfirmButton}
                                onClick={() => deleteItem(itemToDelete.id)}
                            >
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
